import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, useScroll, useTransform, useMotionValueEvent, useReducedMotion } from "framer-motion";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { pickSampleSlug } from "@/lib/sample";
import {
  MessageSquareText, Gavel, FlaskConical, Hammer, Rocket, TrendingUp, ArrowRight, ShieldCheck,
} from "lucide-react";
import { PublicAmbient, KineticHeading, GoldText, DonkeyQuote } from "@/components/public/PublicVisuals";

function pounds(pence) {
  if (pence == null) return null;
  const v = pence / 100;
  return Number.isInteger(v) ? `£${v}` : `£${v.toFixed(2)}`;
}

const EASE = [0.22, 1, 0.36, 1];

const STAGES = [
  { icon: MessageSquareText, title: "Discover", subtitle: "Understand what you already have", plan: "prove", planLabel: "Prove",
    youDo: "Answer an adaptive interview about your skills and experience, knowledge and interests, personal circumstances and constraints, objectives and preferred working style, existing resources and credibility, and how you could reach potential customers.",
    donkeyDoes: "Interviews you properly instead of reacting to a one-line prompt — extracting your transferable capabilities and honestly weighing your credibility.",
    youReceive: "A Personal Opportunity Profile: what the Donkey heard and what you could realistically build around." },
  { icon: Gavel, title: "Decide", subtitle: "Identify something worth building", plan: "prove", planLabel: "Prove", sample: true,
    youDo: "Review three personalised opportunities and the Donkey's honest assessment of each.",
    donkeyDoes: "Produces three genuinely different opportunities matched to you rather than recycled side hustles, tests your assumptions, identifies weaknesses, compares their credibility and commercial potential, and recommends the strongest with clear reasons.",
    youReceive: "The full Donkey's Verdict — an honest recommendation, not automatic encouragement." },
  { icon: FlaskConical, title: "Validate", subtitle: "Test before spending heavily", plan: "prove", planLabel: "Prove", donkey: "sceptical", donkeyLine: "Enthusiasm is not evidence. Let's test it.",
    youDo: "Set your customer and problem assumptions, run validation experiments, record evidence and learning, and decide whether to proceed, adjust or stop.",
    donkeyDoes: "Supports live market research, structures your hypotheses and experiments, and captures the evidence behind each decision.",
    youReceive: "Evidence that customers may care — before you commit serious time or money.",
    note: "Validation reduces uncertainty; it does not guarantee success." },
  { icon: Hammer, title: "Build", subtitle: "Turn the opportunity into a business", plan: "build", planLabel: "Build",
    youDo: "Define the business, its positioning, brand, customer proposition, products or services and pricing inside a dedicated business workspace.",
    donkeyDoes: "Helps develop your business name, brand and logo identity, offer and pricing, business email, SEO foundations and legal templates.",
    youReceive: "A structured business built around the validated opportunity — not an isolated idea.",
    note: "Legal documents are templates, not legal advice. Domain connection and payment setup use external providers and are charged separately." },
  { icon: Rocket, title: "Launch", subtitle: "Put the business in front of customers", plan: "launch", planLabel: "Launch",
    youDo: "Prepare your website and launch, shape your customer-facing offer, and work through the practical launch tasks.",
    donkeyDoes: "Supports website creation and launch preparation, launch strategy and campaign, SEO content, email sequences, ad creative, a social engine and a content calendar with performance reporting.",
    youReceive: "A business prepared to meet real customers — not another document sitting unused.",
    note: "Publishing, email and social content are prepared as drafts/exports and only send or publish once you connect an external email, social or publishing provider. Advertising spend is charged separately." },
  { icon: TrendingUp, title: "Grow & Scale", subtitle: "Keep building what works", plan: "grow", planLabel: "Grow / Scale", donkey: "verdict", donkeyLine: "Now we've got something worth building on.",
    youDo: "Run ongoing marketing and content, capture and nurture leads, manage customers and optimise what works — across multiple businesses as you expand.",
    donkeyDoes: "Grow adds a CRM, lead capture and nurturing, delivery workflows, customer records, a support inbox, DonkeyBot, retention and recurring campaigns, advanced analytics and unit economics, an AI brand representative and growth experiments across up to three businesses. Scale adds advanced permissions, cross-business reporting, higher automation and priority support across up to ten businesses.",
    youReceive: "A practical system for developing the business beyond its initial launch.",
    note: "Marketing, content, email and social features are draft/export-only until the relevant external provider is connected — nothing is published or sent automatically." },
];

const PROGRESSION = [
  { id: "prove", name: "Prove", line: "Discover, decide and begin validation." },
  { id: "build", name: "Build", line: "Turn the chosen opportunity into a structured business." },
  { id: "launch", name: "Launch", line: "Prepare and put the business in front of customers." },
  { id: "grow", name: "Grow", line: "Develop marketing and customer acquisition." },
  { id: "scale", name: "Scale", line: "Expand capacity, activity and collaboration." },
  { id: "enterprise", name: "Enterprise", line: "Contact-only support for larger requirements." },
];

// A single stage anchored on the illuminated path. Lights up when the drawing line reaches it.
function Stage({ s, i, lit, navigate, goSample }) {
  const reduce = useReducedMotion();
  const left = i % 2 === 0; // desktop: alternate sides around the centre line
  const slug = s.title.toLowerCase().replace(/[^a-z]+/g, "-").replace(/(^-|-$)/g, "");
  return (
    <div className="relative lg:grid lg:grid-cols-2 lg:gap-0 pl-16 lg:pl-0" data-testid={`stage-${slug}`}>
      {/* node on the line */}
      <motion.div
        className="absolute left-5 lg:left-1/2 top-6 -translate-x-1/2 z-20"
        initial={false}
        animate={{ scale: lit ? 1 : 0.7 }}
        transition={{ duration: 0.4, ease: EASE }}
      >
        <div className={`w-11 h-11 rounded-full flex items-center justify-center font-mono text-sm border-2 transition-colors duration-500
          ${lit ? "bg-donkey-black border-donkey-orange text-donkey-orange shadow-[0_0_24px_rgba(249,115,22,0.55)]" : "bg-donkey-black border-white/20 text-donkey-grey"}`}>
          0{i + 1}
        </div>
      </motion.div>

      {/* card — enters from its side, settles */}
      <motion.div
        initial={reduce ? false : { opacity: 0, x: left ? -40 : 40, y: 10 }}
        whileInView={{ opacity: 1, x: 0, y: 0 }}
        viewport={{ once: true, amount: 0.4 }}
        transition={{ duration: 0.6, ease: EASE }}
        className={`${left ? "lg:col-start-1 lg:pr-14 lg:text-right" : "lg:col-start-2 lg:pl-14"}`}
      >
        <div className={`card-donkey rounded-2xl p-6 sm:p-7 transition-colors duration-500 ${lit ? "border-donkey-orange/40" : ""}`}>
          <div className={`flex items-start gap-2.5 mb-4 ${left ? "lg:flex-row-reverse lg:text-right" : ""}`}>
            <s.icon className="w-5 h-5 text-donkey-orange shrink-0 mt-0.5" />
            <div className="min-w-0">
              <div className={`flex items-center gap-2 ${left ? "lg:justify-end" : ""}`}>
                <h2 className="font-heading font-bold text-xl sm:text-2xl text-donkey-warm leading-tight">{s.title}</h2>
                <span className="text-[10px] uppercase tracking-wider text-donkey-orange border border-donkey-orange/40 rounded px-1.5 py-0.5 shrink-0 whitespace-nowrap">{s.planLabel}</span>
              </div>
              <p className="text-sm text-donkey-grey">{s.subtitle}</p>
            </div>
          </div>
          <div className={`space-y-4 ${left ? "lg:text-right" : ""}`}>
            <div><p className="text-[11px] uppercase tracking-wider text-donkey-orange/80 mb-1">What you do</p><p className="text-sm text-donkey-warm/85 leading-relaxed">{s.youDo}</p></div>
            <div><p className="text-[11px] uppercase tracking-wider text-donkey-orange/80 mb-1">What the Donkey does</p><p className="text-sm text-donkey-warm/85 leading-relaxed">{s.donkeyDoes}</p></div>
            <div className={`${left ? "lg:border-r-2 lg:border-l-0 lg:pr-3 lg:pl-0" : "border-l-2 pl-3"} border-donkey-orange/50 pl-3`}><p className="text-[11px] uppercase tracking-wider text-donkey-orange/80 mb-1">What you receive</p><p className="text-sm text-donkey-warm leading-relaxed">{s.youReceive}</p></div>
          </div>
          {s.note && <p className="text-xs text-donkey-grey mt-4 leading-relaxed">{s.note}</p>}
          <div className={`mt-5 pt-4 border-t border-white/10 flex flex-wrap items-center gap-x-5 gap-y-2 ${left ? "lg:justify-end" : ""}`}>
            <button onClick={() => navigate("/pricing")} className="text-sm text-donkey-orange hover:underline inline-flex items-center gap-1" data-testid={`stage-plan-link-${s.plan}`}>
              View the {s.planLabel} plan <ArrowRight className="w-3.5 h-3.5" />
            </button>
            {s.sample && <button onClick={goSample} className="text-sm text-donkey-warm/80 hover:text-donkey-warm hover:underline" data-testid="stage-sample-link">See a sample Donkey's Verdict</button>}
          </div>

          {/* Donkey reaction at selected stages only (not everywhere) */}
          {s.donkey && (
            <div className={`mt-5 flex items-center gap-3 ${left ? "lg:flex-row-reverse lg:text-right" : ""}`}>
              <img src={`/mascot/${s.donkey}.png`} alt="" className="h-16 w-auto object-contain shrink-0 drop-shadow-[0_0_24px_rgba(249,115,22,0.3)]" />
              <p className={`text-sm italic ${s.donkey === "sceptical" ? "text-donkey-amber" : "text-donkey-green"}`}>“{s.donkeyLine}”</p>
            </div>
          )}
        </div>
      </motion.div>
    </div>
  );
}

export default function HowItWorks() {
  const navigate = useNavigate();
  const reduce = useReducedMotion();
  const [prices, setPrices] = useState({});
  const [active, setActive] = useState(0);
  const pathRef = useRef(null);

  const { scrollYProgress } = useScroll({ target: pathRef, offset: ["start 0.8", "end 0.4"] });
  const fillHeight = useTransform(scrollYProgress, [0, 1], ["0%", "100%"]);
  useMotionValueEvent(scrollYProgress, "change", (v) => {
    setActive(Math.min(STAGES.length - 1, Math.round(v * (STAGES.length - 1))));
  });

  useEffect(() => {
    api.get("/plans").then(({ data }) => {
      const map = {};
      (data.plans || []).forEach((p) => { map[p.id] = p.price_month; });
      setPrices(map);
    }).catch(() => {});
  }, []);
  useEffect(() => { if (reduce) setActive(STAGES.length - 1); }, [reduce]);

  const goSample = () => navigate(`/example/${pickSampleSlug()}`);

  return (
    <div className="relative overflow-hidden">
      <PublicAmbient intensity={0.4} showGrid />
      <div className="relative z-10 mx-auto max-w-5xl px-5 sm:px-8 py-16">
        <p className="text-xs uppercase tracking-[0.2em] text-donkey-orange mb-4">The Journey</p>
        <KineticHeading text="From “I could start something” to a business built around you." className="text-4xl sm:text-5xl leading-[1.05]" />
        <p className="mt-6 text-donkey-grey max-w-2xl leading-relaxed">
          The Honest Donkey doesn't throw a list of generic ideas at you and disappear. It helps you discover what
          you could credibly build, decide what is worth pursuing, test it before wasting money, and then build,
          launch and grow the business properly.
        </p>
        <p className="mt-6 font-heading font-bold text-lg sm:text-xl text-donkey-warm">
          One Donkey. The whole journey. <GoldText>Without the bullshit.</GoldText>
        </p>
        <DonkeyQuote className="mt-6 max-w-xl" quotes={["Most people start with the idea. I start with you.", "Every stage earns its place — or it doesn't happen.", "No leaps of faith. One evidenced step at a time."]} />

        {/* ===== THE ILLUMINATED PATH — physically draws as you travel down it ===== */}
        <div ref={pathRef} className="relative mt-20 space-y-12 lg:space-y-16">
          {/* track + drawing fill (centre on desktop, left on mobile) */}
          <div className="absolute left-5 lg:left-1/2 top-0 bottom-0 w-[2px] -translate-x-1/2 bg-white/10" aria-hidden />
          <motion.div
            className="absolute left-5 lg:left-1/2 top-0 w-[2px] -translate-x-1/2 bg-gradient-to-b from-donkey-orange via-donkey-amber to-donkey-warmorange rounded-full"
            style={{ height: reduce ? "100%" : fillHeight, boxShadow: "0 0 16px rgba(249,115,22,0.6)" }}
            aria-hidden
          >
            {!reduce && <span className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-2.5 h-2.5 rounded-full bg-donkey-warmorange shadow-[0_0_14px_6px_rgba(255,210,122,0.55)]" />}
          </motion.div>

          {STAGES.map((s, i) => (
            <Stage key={s.title} s={s} i={i} lit={active >= i} navigate={navigate} goSample={goSample} />
          ))}

          {/* destination marker */}
          <div className="relative pl-16 lg:pl-0 lg:flex lg:justify-center">
            <div className="absolute left-5 lg:left-1/2 top-2 -translate-x-1/2 w-5 h-5 rounded-full bg-donkey-orange shadow-[0_0_22px_rgba(249,115,22,0.7)]" />
            <p className="lg:text-center font-heading font-bold text-donkey-warm text-lg pt-0.5">A business built around you.</p>
          </div>
        </div>

        {/* Honest plan progression */}
        <section className="mt-20" data-testid="plan-progression">
          <div className="w-16 h-1 rounded-full bg-donkey-orange mb-6" />
          <h2 className="font-heading font-bold text-2xl sm:text-3xl tracking-tight text-donkey-warm">How the plans progress</h2>
          <p className="mt-3 text-donkey-grey max-w-2xl leading-relaxed">
            You do not need every plan on day one. Start at the stage you genuinely need and move forward when the business is ready.
          </p>
          <div className="mt-8 space-y-3">
            {PROGRESSION.map((p) => (
              <div key={p.id} className="group card-donkey rounded-xl px-5 py-4 flex items-center justify-between gap-4 hover:border-donkey-orange/50 transition-colors">
                <div className="min-w-0">
                  <p className="font-heading font-semibold text-donkey-warm">{p.name}</p>
                  <p className="text-sm text-donkey-grey">{p.line}</p>
                </div>
                <div className="shrink-0 text-right">
                  {p.id === "enterprise" ? (
                    <button onClick={() => navigate("/contact")} className="text-sm text-donkey-orange hover:underline">Contact us</button>
                  ) : (
                    <span className="font-mono text-donkey-warm">{pounds(prices[p.id]) || ""}<span className="text-xs text-donkey-grey">/mo</span></span>
                  )}
                </div>
              </div>
            ))}
          </div>
          <p className="text-xs text-donkey-grey mt-4">Prices are the current monthly GBP charges and are confirmed with any applicable taxes at checkout.</p>
        </section>

        {/* Final CTA — the Donkey presents the destination */}
        <section className="mt-20 relative overflow-hidden text-center card-donkey rounded-2xl p-10">
          <div className="absolute left-1/2 -translate-x-1/2 bottom-0 w-[28rem] h-[12rem] rounded-[50%] bg-donkey-orange/20 blur-[70px] pointer-events-none" aria-hidden />
          <img src="/mascot/verdict.png" alt="" className="relative mx-auto mb-6 h-40 w-auto object-contain drop-shadow-[0_14px_40px_rgba(0,0,0,0.5)]" />
          <h2 className="relative font-heading font-bold text-2xl sm:text-3xl text-donkey-warm max-w-2xl mx-auto leading-snug">Ready to find out what you could credibly build?</h2>
          <div className="relative mt-8 flex flex-wrap gap-4 justify-center">
            <Button className="bg-donkey-orange text-donkey-black hover:bg-donkey-deeporange font-bold rounded-full px-8 h-12 shadow-[0_4px_20px_rgba(249,115,22,0.3)] hover:-translate-y-0.5 transition-all" onClick={() => navigate("/pricing")} data-testid="journey-prove-cta">
              Start with Prove — £29 <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
            <Button variant="outline" className="border-donkey-line bg-transparent text-donkey-warm hover:border-donkey-orange/60 hover:bg-white/5 rounded-full px-7 h-12" onClick={goSample} data-testid="journey-sample-cta">See a sample Donkey's Verdict</Button>
            <Button variant="outline" className="border-donkey-line bg-transparent text-donkey-warm hover:border-donkey-orange/60 hover:bg-white/5 rounded-full px-7 h-12" onClick={() => navigate("/pricing")} data-testid="journey-plans-cta">View all plans</Button>
          </div>
          <p className="relative mt-6 text-sm text-donkey-grey inline-flex items-center gap-1.5">
            <ShieldCheck className="w-4 h-4 text-donkey-orange" /> £29 per month. Cancel anytime securely through Stripe.
          </p>
        </section>
      </div>
    </div>
  );
}
